import React, { useMemo } from 'react';
import { View, Text } from '../lib/react-native-compat';
import { useAtom } from 'jotai';
import { jobCostDetailAtom, jobUserDataAtom, jobOpenCommitmentsAtom, selectedVersionAtom, projectCompleteAtom, dataSourceVersionAtom } from '../store/company';
import { fNum, pNum } from '../db/company';
import moment from 'moment';
import { Bar } from 'react-chartjs-2';
import { SERIES, SERIES_OTHER, TEXT_SECONDARY, alpha, GRID } from '../styles/chartColors';
import { Chart as ChartJS, CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend } from 'chart.js';

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend);

// The five series colours in their fixed order; a sixth class and beyond is grey
// (docs/STRIKE_STYLE.md, section 7). Labor hours sit on their own axis in ink grey.
const COLORS = SERIES

const card = { backgroundColor: '#fff', borderRadius: 8, border: '1px solid #e2e8f0', padding: 16, marginBottom: 16 }
const hdr = { padding: '6px 10px', fontSize: 12, fontWeight: '700', color: '#1e293b', borderBottom: '2px solid #e2e8f0', textAlign: 'left', whiteSpace: 'nowrap' }
const cell = { padding: '5px 10px', fontSize: 12, color: '#334155', borderBottom: '1px solid #f1f5f9' }
const cellR = { ...cell, textAlign: 'right', fontVariantNumeric: 'tabular-nums' }

const FuelAnalysis = () => {
    const [jobCostDetail] = useAtom(jobCostDetailAtom)
    const [jobUserData] = useAtom(jobUserDataAtom)
    const [jobOpenCommitments] = useAtom(jobOpenCommitmentsAtom)
    const [selectedVersion] = useAtom(selectedVersionAtom)
    const [projectComplete] = useAtom(projectCompleteAtom)
    const [versions] = useAtom(dataSourceVersionAtom)

    // Determine version start so ETC labor only shows on future weeks
    const versionStart = useMemo(() => {
        if (!versions || !selectedVersion) return null
        const v = Array.isArray(versions) ? versions.find(v => v.Version_GUID === selectedVersion) : null
        return v?.Version_Start_DateTime ? moment(v.Version_Start_DateTime).startOf('isoWeek').format('YYYY-MM-DD') : null
    }, [versions, selectedVersion])

    // All fuel + labor data computed in a single pass
    const fuelData = useMemo(() => {
        const fuelRows = Array.isArray(jobCostDetail) ? jobCostDetail.filter(r => r.Cost_Type_Code === '52300') : []
        const laborRows = Array.isArray(jobCostDetail) ? jobCostDetail.filter(r => {
            const ct = r.Cost_Type_Code
            return ct >= '51100' && ct <= '51800'
        }) : []
        const fuelCommitments = Array.isArray(jobOpenCommitments) ? jobOpenCommitments.filter(r => r.Cost_Type_Code === '52300') : []
        const etcRows = Array.isArray(jobUserData) ? jobUserData.filter(d =>
            d?.Cost_Type === '52300' && d?.Forecast_Field_Name === 'etc' && (!d?.Cost_Code || d?.Cost_Code !== '101101')
        ) : []
        const etcTotal = etcRows.reduce((s, d) => s + pNum(d?.Value_Amount), 0)

        let totalActual = 0
        let totalLaborHours = 0
        const weekSet = new Set()
        const groups = {} // { eqClass: { week: cost } }
        const laborHoursByWeek = {} // { week: hours }
        const fuelCostByWeek = {} // { week: totalFuelCost } for ratio calc

        fuelRows.forEach(r => {
            const cost = pNum(r.Job_Cost)
            totalActual += cost
            const d = moment(r.GL_Date || r.Transaction_Date)
            if (!d.isValid()) return
            const wk = d.clone().startOf('isoWeek').format('YYYY-MM-DD')
            weekSet.add(wk)
            fuelCostByWeek[wk] = (fuelCostByWeek[wk] || 0) + cost
            const eqClass = r.Equipment_Classification_Tier_Name || 'Unclassified'
            if (!groups[eqClass]) groups[eqClass] = {}
            groups[eqClass][wk] = (groups[eqClass][wk] || 0) + cost
        })

        laborRows.forEach(r => {
            const hrs = pNum(r.Total_Hours)
            totalLaborHours += hrs
            const d = moment(r.GL_Date || r.Transaction_Date)
            if (!d.isValid()) return
            const wk = d.clone().startOf('isoWeek').format('YYYY-MM-DD')
            weekSet.add(wk)
            laborHoursByWeek[wk] = (laborHoursByWeek[wk] || 0) + hrs
        })

        // Labor ETC hours from burn tool data — only future weeks
        let totalEtcLaborHours = 0
        const etcLaborHoursByWeek = {}
        if (Array.isArray(jobUserData)) {
            jobUserData.forEach(d => {
                if ((d?.Forecast_Tab === 'Burn Tool Labor' || d?.Forecast_Tab === 'Burn Tool Labor Phased') && d?.Version_GUID === selectedVersion) {
                    const isPhasedKey = d.Forecast_Row_ID?.includes('=')
                    const week = isPhasedKey ? d.Forecast_Row_ID.split('=')[1] : d.Forecast_Row_ID?.split('*')[0]
                    const h = pNum(d?.Number_Hours || 0)
                    const cS = pNum(d?.Number_Units || 0)
                    const withinProject = projectComplete ? moment(week).isBefore(moment(projectComplete).endOf('week')) : true
                    const isFuture = versionStart ? week >= versionStart : true
                    if (week && h && cS && withinProject && isFuture) {
                        const wk = moment(week).startOf('isoWeek').format('YYYY-MM-DD')
                        weekSet.add(wk)
                        const hrs = h * cS
                        etcLaborHoursByWeek[wk] = (etcLaborHoursByWeek[wk] || 0) + hrs
                        totalEtcLaborHours += hrs
                    }
                }
            })
        }

        const openTotal = fuelCommitments.reduce((s, r) => s + pNum(r.PO_True_Open_Amount || r.PO_Open_Amount), 0)
        const weeks = [...weekSet].sort()
        const labels = Object.keys(groups).sort((a, b) => {
            const aT = Object.values(groups[a]).reduce((s, v) => s + v, 0)
            const bT = Object.values(groups[b]).reduce((s, v) => s + v, 0)
            return bT - aT
        })

        return { fuelRows, laborRows, fuelCommitments, etcRows, etcTotal, totalActual, totalLaborHours, totalEtcLaborHours, openTotal, weeks, labels, groups, laborHoursByWeek, etcLaborHoursByWeek, fuelCostByWeek }
    }, [jobCostDetail, jobOpenCommitments, jobUserData, selectedVersion, projectComplete, versionStart])

    const { fuelRows, laborRows, fuelCommitments, etcRows, etcTotal, totalActual, totalLaborHours, totalEtcLaborHours, openTotal, weeks, labels, groups, laborHoursByWeek, etcLaborHoursByWeek, fuelCostByWeek } = fuelData
    const totalForecast = totalActual + openTotal + etcTotal
    const overallRatio = totalLaborHours > 0 ? totalActual / totalLaborHours : 0

    // Chart data — memoized to prevent re-render loops
    const eqChartData = useMemo(() => ({
        labels: weeks.map(w => moment(w).format('M/D')),
        datasets: [
            // Labor actual hours — on right Y axis, own stack
            {
                label: 'Actual Labor Hours',
                data: weeks.map(w => laborHoursByWeek[w] || 0),
                backgroundColor: alpha(TEXT_SECONDARY, 0.8),
                borderColor: TEXT_SECONDARY,
                borderWidth: 1,
                stack: 'labor',
                yAxisID: 'yHours',
            },
            // Labor ETC hours — stacked on top of actual labor hours
            {
                label: 'ETC Labor Hours',
                data: weeks.map(w => etcLaborHoursByWeek[w] || 0),
                backgroundColor: alpha(TEXT_SECONDARY, 0.4),
                borderColor: TEXT_SECONDARY,
                borderWidth: 1,
                borderDash: [4, 2],
                stack: 'labor',
                yAxisID: 'yHours',
            },
            // Fuel equipment class bars — stacked together on left Y axis
            ...labels.map((label, i) => ({
                label: 'Fuel – ' + label,
                data: weeks.map(w => groups[label][w] || 0),
                backgroundColor: alpha(i < COLORS.length ? COLORS[i] : SERIES_OTHER, 0.6),
                borderColor: i < COLORS.length ? COLORS[i] : SERIES_OTHER,
                borderWidth: 1,
                stack: 'fuel',
                yAxisID: 'yCost',
            }))
        ]
    }), [weeks, labels, groups, laborHoursByWeek, etcLaborHoursByWeek])

    const eqChartOptions = useMemo(() => ({
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
            legend: { position: 'top', labels: { usePointStyle: true, padding: 12, font: { size: 10 } } },
            tooltip: {
                callbacks: {
                    label: ctx => {
                        const isHours = ctx.dataset.yAxisID === 'yHours'
                        return ctx.dataset.label + ': ' + (isHours ? fNum(ctx.parsed.y) + ' hrs' : '$' + fNum(ctx.parsed.y))
                    },
                }
            }
        },
        scales: {
            x: { stacked: true, ticks: { font: { size: 10 } }, grid: { display: false } },
            yCost: { stacked: true, position: 'left', beginAtZero: true, ticks: { callback: v => '$' + fNum(v), font: { size: 10 } }, grid: { color: GRID }, title: { display: true, text: 'Fuel Cost ($)', font: { size: 11 } } },
            yHours: { stacked: true, position: 'right', beginAtZero: true, ticks: { callback: v => fNum(v) + ' hrs', font: { size: 10 } }, grid: { drawOnChartArea: false }, title: { display: true, text: 'Labor Hours', font: { size: 11 } } }
        }
    }), [])

    return (
        <View style={{ margin: 30, marginTop: 10, marginRight: 200, marginBottom: 200 }}>
            {/* Summary Cards */}
            <View style={{ flexDirection: 'row', gap: 16, marginBottom: 20, flexWrap: 'wrap' }}>
                <View style={{ ...card, flex: 1, minWidth: 180 }}>
                    <Text style={{ fontSize: 11, color: '#64748b', fontWeight: '600', textTransform: 'uppercase', letterSpacing: 0.5 }}>Actual Fuel Cost</Text>
                    <Text style={{ fontSize: 22, fontWeight: '700', color: '#1e293b', marginTop: 4 }}>{fNum(totalActual)}</Text>
                    <Text style={{ fontSize: 11, color: '#94a3b8', marginTop: 2 }}>{fuelRows.length} transactions</Text>
                </View>
                <View style={{ ...card, flex: 1, minWidth: 180 }}>
                    <Text style={{ fontSize: 11, color: '#64748b', fontWeight: '600', textTransform: 'uppercase', letterSpacing: 0.5 }}>Actual Labor Hours</Text>
                    <Text style={{ fontSize: 22, fontWeight: '700', color: '#6366f1', marginTop: 4 }}>{fNum(totalLaborHours)}</Text>
                    <Text style={{ fontSize: 11, color: '#94a3b8', marginTop: 2 }}>{laborRows.length} transactions</Text>
                </View>
                <View style={{ ...card, flex: 1, minWidth: 180 }}>
                    <Text style={{ fontSize: 11, color: '#64748b', fontWeight: '600', textTransform: 'uppercase', letterSpacing: 0.5 }}>ETC Labor Hours</Text>
                    <Text style={{ fontSize: 22, fontWeight: '700', color: '#a5b4fc', marginTop: 4 }}>{fNum(totalEtcLaborHours)}</Text>
                    <Text style={{ fontSize: 11, color: '#94a3b8', marginTop: 2 }}>From burn tool</Text>
                </View>
                <View style={{ ...card, flex: 1, minWidth: 180 }}>
                    <Text style={{ fontSize: 11, color: '#64748b', fontWeight: '600', textTransform: 'uppercase', letterSpacing: 0.5 }}>Open Commitments</Text>
                    <Text style={{ fontSize: 22, fontWeight: '700', color: '#f59e0b', marginTop: 4 }}>{fNum(openTotal)}</Text>
                    <Text style={{ fontSize: 11, color: '#94a3b8', marginTop: 2 }}>{fuelCommitments.length} open PO lines</Text>
                </View>
                <View style={{ ...card, flex: 1, minWidth: 180 }}>
                    <Text style={{ fontSize: 11, color: '#64748b', fontWeight: '600', textTransform: 'uppercase', letterSpacing: 0.5 }}>Fuel ETC</Text>
                    <Text style={{ fontSize: 22, fontWeight: '700', color: '#2563eb', marginTop: 4 }}>{fNum(etcTotal)}</Text>
                    <Text style={{ fontSize: 11, color: '#94a3b8', marginTop: 2 }}>{etcRows.length} ETC entries</Text>
                </View>
                <View style={{ ...card, flex: 1, minWidth: 180 }}>
                    <Text style={{ fontSize: 11, color: '#64748b', fontWeight: '600', textTransform: 'uppercase', letterSpacing: 0.5 }}>Total Forecast</Text>
                    <Text style={{ fontSize: 22, fontWeight: '700', color: '#1e293b', marginTop: 4 }}>{fNum(totalForecast)}</Text>
                    <Text style={{ fontSize: 11, color: '#94a3b8', marginTop: 2 }}>Actual + Open + ETC</Text>
                </View>
            </View>

            {/* Chart: Equipment Classification — Actual Fuel Cost by Week */}
            {weeks.length > 0 && <View style={card}>
                <Text style={{ fontSize: 14, fontWeight: '700', color: '#1e293b', marginBottom: 12 }}>Fuel Cost & Labor Hours by Week</Text>
                <div style={{ height: 360 }}>
                    <Bar data={eqChartData} options={eqChartOptions} />
                </div>
            </View>}

            {/* Summary Table */}
            {labels.length > 0 && <View style={card}>
                <Text style={{ fontSize: 14, fontWeight: '700', color: '#1e293b', marginBottom: 10 }}>Actual Fuel Cost Detail</Text>
                <div style={{ overflowX: 'auto' }}>
                    <table style={{ borderCollapse: 'collapse', fontSize: 11, width: '100%' }}>
                        <thead>
                            <tr style={{ backgroundColor: '#f8fafc' }}>
                                <th style={{ ...hdr, position: 'sticky', left: 0, backgroundColor: '#f8fafc', zIndex: 2, minWidth: 160 }}>Equipment Class</th>
                                {weeks.map(w => (
                                    <th key={w} style={{ ...hdr, textAlign: 'right', minWidth: 80, fontSize: 10 }}>{moment(w).format('M/D')}</th>
                                ))}
                                <th style={{ ...hdr, textAlign: 'right', minWidth: 90, backgroundColor: '#eef2ff' }}>Total</th>
                            </tr>
                        </thead>
                        <tbody>
                            {labels.map((label, i) => {
                                const rowTotal = weeks.reduce((s, w) => s + (groups[label][w] || 0), 0)
                                return (
                                    <tr key={label} style={{ backgroundColor: i % 2 === 0 ? '#fff' : '#fafbfc' }}>
                                        <td style={{ ...cell, fontWeight: '600', position: 'sticky', left: 0, backgroundColor: i % 2 === 0 ? '#fff' : '#fafbfc', zIndex: 1 }}>
                                            <span style={{ display: 'inline-block', width: 8, height: 8, borderRadius: 2, backgroundColor: COLORS[i % COLORS.length], marginRight: 6 }} />
                                            {label}
                                        </td>
                                        {weeks.map(w => {
                                            const val = groups[label][w] || 0
                                            return <td key={w} style={{ ...cellR, color: val > 0 ? '#334155' : '#cbd5e1' }}>{val > 0 ? fNum(val) : '-'}</td>
                                        })}
                                        <td style={{ ...cellR, fontWeight: '700', backgroundColor: '#f0f4ff' }}>{fNum(rowTotal)}</td>
                                    </tr>
                                )
                            })}
                            <tr style={{ backgroundColor: '#f0f4ff', fontWeight: '700' }}>
                                <td style={{ ...cell, fontWeight: '700', position: 'sticky', left: 0, backgroundColor: '#f0f4ff', zIndex: 1 }}>Total Fuel</td>
                                {weeks.map(w => {
                                    const wkTotal = labels.reduce((s, l) => s + (groups[l][w] || 0), 0)
                                    return <td key={w} style={{ ...cellR, fontWeight: '700' }}>{wkTotal > 0 ? fNum(wkTotal) : '-'}</td>
                                })}
                                <td style={{ ...cellR, fontWeight: '700', backgroundColor: '#e0e7ff' }}>{fNum(totalActual)}</td>
                            </tr>
                            <tr style={{ backgroundColor: '#f5f3ff' }}>
                                <td style={{ ...cell, fontWeight: '600', position: 'sticky', left: 0, backgroundColor: '#f5f3ff', zIndex: 1, color: '#6366f1' }}>Labor Hours</td>
                                {weeks.map(w => {
                                    const hrs = laborHoursByWeek[w] || 0
                                    return <td key={w} style={{ ...cellR, color: hrs > 0 ? '#6366f1' : '#cbd5e1' }}>{hrs > 0 ? fNum(hrs) : '-'}</td>
                                })}
                                <td style={{ ...cellR, fontWeight: '700', backgroundColor: '#ede9fe', color: '#6366f1' }}>{fNum(totalLaborHours)}</td>
                            </tr>
                            <tr style={{ backgroundColor: '#fefce8' }}>
                                <td style={{ ...cell, fontWeight: '600', position: 'sticky', left: 0, backgroundColor: '#fefce8', zIndex: 1, color: '#92400e' }}>Fuel $ / Labor Hr</td>
                                {weeks.map(w => {
                                    const hrs = laborHoursByWeek[w] || 0
                                    const fuel = fuelCostByWeek[w] || 0
                                    const ratio = hrs > 0 ? fuel / hrs : 0
                                    return <td key={w} style={{ ...cellR, color: ratio > 0 ? '#92400e' : '#cbd5e1', fontWeight: '600' }}>{ratio > 0 ? '$' + fNum(ratio) : '-'}</td>
                                })}
                                <td style={{ ...cellR, fontWeight: '700', backgroundColor: '#fef3c7', color: '#92400e' }}>{overallRatio > 0 ? '$' + fNum(overallRatio) : '-'}</td>
                            </tr>
                        </tbody>
                    </table>
                </div>
            </View>}

            {fuelRows.length === 0 && etcRows.length === 0 && (
                <View style={{ ...card, alignItems: 'center', padding: 40 }}>
                    <Text style={{ fontSize: 14, color: '#94a3b8' }}>No fuel data (52300) found for this job.</Text>
                </View>
            )}
        </View>
    )
}

export default FuelAnalysis
